import { Box, Dialog, Grid, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { icons } from 'src/assets'
import { Loader } from 'src/components/UI'
import Tag from 'src/components/UI/CustomUI/atoms/Tag'
import { clientActions, estimateActions } from 'src/ducks/actions'
import { employeeIsAdmin, employeeIsBusinessDevManager, getClientIsHomeowner, getEstimate } from 'src/ducks/selectors'
import {
    APPROVAL_TYPE,
    companyRoles,
    CONTACT_ROLE,
    ESTIMATE_APPROVAL_STATUS,
    ESTIMATE_STATUS,
    FILE_TYPE,
    homeownerRoles
} from 'src/helpers'
import ApprovalFlowTrackerStepper from './Stepper'
import styles from './styles.module.scss'

const ApprovalFlowTracker = () => {
  const dispatch = useDispatch()
  const estimate = useSelector(getEstimate())
  const contacts = estimate?.properties?.contacts || []
  const isPayAtClose = estimate?.properties?.payAtClose
  const propertyUnderContract = estimate?.properties?.propertyUnderContract
  const userApproving = contacts.find((c) => c.approvalFlowInProgress)
  const clientIsHomeowner = useSelector(getClientIsHomeowner)
  const [showApproveFinancialModal, setShowApproveFinancialModal] = useState(false)
  const [showReStartPayAtCloseModal, setShowReStartPayAtCloseModal] = useState(false)
  const [showReStartPayAtCompletionModal, setShowReStartPayAtCompletionModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const agentContact = contacts.find((c) =>
    companyRoles.includes(c.role as CONTACT_ROLE)
  )
  const agentContacts = contacts.filter((c) =>
    companyRoles.includes(c.role as CONTACT_ROLE)
  )

  const homeownerContact =
    contacts.find(
      (c) =>
        homeownerRoles.includes(c.role as CONTACT_ROLE) &&
        [APPROVAL_TYPE.REQUIRED, APPROVAL_TYPE.APPROVED].includes(
          c.approvalType as APPROVAL_TYPE
        )
    ) || contacts.find((c) => homeownerRoles.includes(c.role as CONTACT_ROLE))

  const isHomewardCXSupport = homeownerContact?.email
    ? ['cxsupport@test.com', 'cxsupport@homeward.com'].includes(
      homeownerContact?.email
    )
    : false

  const isBelow10K = estimate?.properties?.totalWithTax && estimate?.properties?.installmentThreshold && estimate?.properties?.totalWithTax < estimate?.properties?.installmentThreshold

  const agentIsRequired = agentContact && estimate?.properties.payAtClose && isBelow10K

  const isEmployeeAdmin = useSelector(employeeIsAdmin)
  const isEmployeeBusinessDevManager = useSelector(employeeIsBusinessDevManager)

  const agentIsHomeowner =
    !clientIsHomeowner &&
    homeownerContact?.contactId &&
    homeownerContact?.role &&
    [
      CONTACT_ROLE.HOMEOWNER,
      CONTACT_ROLE.HOME_BUYER,
      CONTACT_ROLE.HOME_SELLER,
      CONTACT_ROLE.AGENT_HOMEOWNER,
    ].includes(homeownerContact?.role as CONTACT_ROLE)

  const listingAgreement = estimate?.properties?.files?.find(
    (file) => file.fileType === FILE_TYPE.LISTING_AGREEMENT
  )
  const comparableMarketAnalysis = estimate?.properties?.files?.find(
    (file) => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS
  )

  const approvalFlowStarted =
    !isEmpty(userApproving) ||
    isPayAtClose ||
    [
      ESTIMATE_APPROVAL_STATUS.APPROVED,
      ESTIMATE_APPROVAL_STATUS.PROCESSING_ACH_PAYMENT,
    ].includes(estimate?.approvalStatus as ESTIMATE_APPROVAL_STATUS)

  const approvedFinancially = estimate?.properties.financingStatus === "APPROVED";

  const handleCheckApproveFinancing = () => {
    setShowApproveFinancialModal(true)
  }

  const handleReStartPayAtClose = () => {
    setShowReStartPayAtCloseModal(true)
  }

  const handleConfirmReStartPayAtClose = () => {
    setLoading(true)
    dispatch(estimateActions.resetEstimateApprovalFlow({
      estimateId: estimate?.id as string || '',
      payAtClose: true
    }, (succ) => {
      setShowReStartPayAtCloseModal(false)
      setLoading(false)
    }))
  }

  const handleConfirmReStartPayAtCompletion = () => {
    setLoading(true)
    dispatch(estimateActions.resetEstimateApprovalFlow({
      estimateId: estimate?.id as string || '',
      payAtClose: false
    }, (succ) => {
      setShowReStartPayAtCompletionModal(false)
      setLoading(false)
    }))
  }


  const handleReStartPayAtCompletion = () => {
    setShowReStartPayAtCompletionModal(true);
  }

  const handleSendRejectFinancing = () => {
    setLoading(true)
    dispatch(estimateActions.updateEstimateByPath([
      // { op: 'replace', path: '/properties/financingApproved', value: false },
      { op: 'replace', path: '/properties/financingStatus', value: 'REJECTED' },
      { op: 'replace', path: '/status', value: ESTIMATE_STATUS.RELEASED }
    ], (succ, estimateId) => {
      setShowApproveFinancialModal(false)
      setLoading(false)
    }))
  }

  const handleSendApproveFinancing = () => {
    setLoading(true)
    dispatch(estimateActions.updateEstimateByPath([
      // { op: 'replace', path: '/properties/financingApproved', value: true },
      { op: 'replace', path: '/properties/financingStatus', value: 'APPROVED' },
      { op: 'replace', path: '/status', value: ESTIMATE_STATUS.RELEASED }
    ], (succ, estimateId) => {
      setShowApproveFinancialModal(false)
      setLoading(false)
    }))
  }

  const disabledFinancing = !isEmployeeAdmin && !isEmployeeBusinessDevManager

  useEffect(() => {
    //fetch client data for Agent as Homeowner flow validations
    homeownerContact?.contactId &&
      dispatch(clientActions.fetchClient(homeownerContact?.contactId))
  }, [dispatch, homeownerContact])

  const disabledReStartPayAtClose = estimate?.status === ESTIMATE_STATUS.AWAITING_FINANCING || estimate?.properties.financingStatus === "PAY_AT_CLOSE_RESTARTED"
  const disabledReStartPayAtCompletion = estimate?.status === ESTIMATE_STATUS.AWAITING_FINANCING || estimate?.properties.financingStatus === "PAY_AT_COMPLETION_RESTARTED"
  const financingRejected = estimate?.properties.financingStatus === "REJECTED" || estimate?.properties.financingStatus === "PAY_AT_CLOSE_RESTARTED" || estimate?.properties.financingStatus === "PAY_AT_COMPLETION_RESTARTED"

  const steps = [
    {
      label: `Estimate\nReleased`,
      completed: Boolean(estimate?.properties.estimateDeliveredOn),
      completedOn: estimate?.properties.estimateDeliveredOn,
    },
    {
      label: `Approval Flow\nStarted`,
      completed: approvalFlowStarted ?? true,
      completedOn: estimate?.approvalFlowStartedOn
    },
  ] as any

  if ((isPayAtClose && !isBelow10K) || estimate?.properties.financingRequestedOn) {
    if (!financingRejected) {
      steps.push({
        label: `Awaiting\nFinancing`,
        completed: true,
        completedOn: estimate.properties.financingRequestedOn || null,
      })
    }

    steps.push({
      label: `Financing\nApproved`,
      completed: approvedFinancially,
      rejected: financingRejected,
      completedOn: approvedFinancially ? estimate.properties.financingApprovedOn : estimate.properties.financingRejectedOn,
      action: approvedFinancially ? undefined : <>
        <Box onClick={handleCheckApproveFinancing} className={`${styles.buttonContainer} ${(financingRejected || disabledFinancing) && styles.disabled}`}>
          <Typography style={{ fontSize: '11px', color: "#fff", fontWeight: 'bold', textAlign: 'center' }}>{`${financingRejected ? "Financing Rejected" : "Financing Approved?"}`}</Typography>
          {estimate.properties.financingStatus === 'APPROVED' && <icons.CheckCircle className={styles.ApprovalFlowTracker__icon} />}
          {financingRejected && <icons.Close className={styles.ApprovalFlowTracker__icon_rejected} />}
        </Box>
        {estimate.properties.financingStatus === "PAY_AT_COMPLETION_RESTARTED" || estimate.properties.financingStatus === "APPROVED" ? <></> : <Box onClick={handleReStartPayAtClose} className={`${styles.buttonRePayAtCloseContainer} ${(disabledReStartPayAtClose || disabledFinancing) && styles.disabled}`}>
          <Typography style={{ fontSize: '11px', color: "#fff", fontWeight: 'bold', textAlign: 'center' }}>Re-start Pay at Close</Typography>
        </Box>}
        {estimate.properties.financingStatus === "PAY_AT_CLOSE_RESTARTED" || estimate.properties.financingStatus === "APPROVED" ? <></> : <Box onClick={handleReStartPayAtCompletion} className={`${styles.buttonRePayAtCompletionContainer} ${(disabledReStartPayAtCompletion || disabledFinancing) && styles.disabled}`}>
          <Typography style={{ fontSize: '11px', color: "#fff", fontWeight: 'bold', textAlign: 'center' }}>Re-start Pay at Completion</Typography>
        </Box>}
      </>
    })
  }

  const paymentTag = {
    background: isPayAtClose
      ? 'var(--bosscat-blue-600)'
      : isPayAtClose === null
        ? 'var(--gray200)'
        : 'var(--gray700)',
    label: isPayAtClose
      ? 'Pay at Close'
      : isPayAtClose === null
        ? 'Awaiting Payment Method'
        : 'Standard Payment',
  }

  if (agentIsRequired) {
    const listingAgreementDateSigned = estimate?.properties?.files && estimate?.properties?.files.find(file => file.fileType === FILE_TYPE.LISTING_AGREEMENT)?.uploadedOn;
    steps.push({
      label: 'Listing\nAgreement',
      completed: !isEmpty(listingAgreement),
      completedOn: listingAgreementDateSigned
    })

    if (!propertyUnderContract) {
      const cmaDateSigned = estimate?.properties?.files && estimate?.properties?.files.find(file => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS)?.uploadedOn;
      steps.push({
        label: 'Agent\nCMA',
        completed: !isEmpty(comparableMarketAnalysis),
        completedOn: cmaDateSigned
      })
    }

    steps.push({
      label: 'Agent\nAcknowledgement',
      completed:
        (agentContacts.filter(ac => ac?.agentAcknowledgementSignedOn)?.[0]?.agentAcknowledgementSignedOn as number) > 0
          ? true
          : false,
      completedOn: agentContacts.filter(ac => ac?.agentAcknowledgementSignedOn)?.[0]?.agentAcknowledgementSignedOn,
    })
  }

  if (agentIsHomeowner && isPayAtClose) {
    const listingAgreementDateSigned = estimate?.properties?.files && estimate?.properties?.files.find(file => file.fileType === FILE_TYPE.LISTING_AGREEMENT)?.uploadedOn;
    steps.push({
      label: 'Homeowner/Agent\nListing Agreement',
      completed: !isEmpty(listingAgreement),
      completedOn: listingAgreementDateSigned
    })
    if (!propertyUnderContract) {
      const cmaDateSigned = estimate?.properties?.files && estimate?.properties?.files.find(file => file.fileType === FILE_TYPE.COMPARABLE_MARKET_ANALYSIS)?.uploadedOn;
      steps.push({
        label: 'Homeowner/Agent\nCMA',
        completed: !isEmpty(comparableMarketAnalysis),
        completedOn: cmaDateSigned
      })
    }
  }

  if (!isHomewardCXSupport) {
    steps.push(
      {
        label: agentIsHomeowner
          ? 'Homeowner/Agent\nService Agreement'
          : `${homeownerContact?.role === CONTACT_ROLE.HOMEOWNER
            ? 'Homeowner'
            : 'Homebuyer'
          }\nService\n Agreement`,
        completed:
          (homeownerContact?.contractSignedOn as number) > 0 ? true : false,
        completedOn: homeownerContact?.contractSignedOn,
      },
      // {
      //   label: `Deposit\nPaid`,
      //   completed: homeownerContact?.approvalType === APPROVAL_TYPE.APPROVED,
      //   completedOn: estimate?.depositPaidOn
      // }
    )
  }

  steps.push({
    label: 'Estimate\nApproved',
    completed: estimate?.approvalStatus === ESTIMATE_APPROVAL_STATUS.APPROVED,
    completedOn: estimate?.approvedOn,
  })

  return (
    <Grid
      item
      container
      direction={'column'}
      gap="1rem"
      className={styles.ApprovalFlowTracker}
    >
      <Typography variant="h6Bold">Approval Flow Tracker</Typography>
      <Box style={{ display: 'flex', gap: '.5rem' }}>
        <Tag
          width="fit-content"
          variant="body1ExtraBold"
          sx={{
            border: 'none !important',
            borderRadius: '24px',
            padding: '.3rem .8rem',
          }}
          background={paymentTag.background}
          color={isPayAtClose === null ? 'var(--black)' : 'white'}
        >
          {paymentTag.label}
        </Tag>
        {estimate?.approvalStatus ===
          ESTIMATE_APPROVAL_STATUS.PROCESSING_ACH_PAYMENT && (
            <Tag
              width="fit-content"
              variant="body1ExtraBold"
              sx={{
                border: 'none !important',
                borderRadius: '24px',
                padding: '.3rem .8rem',
              }}
              background={'#3C2CA4'}
              color={isPayAtClose === null ? 'var(--black)' : 'white'}
            >
              Processing ACH Payment
            </Tag>
          )}
      </Box>
      <ApprovalFlowTrackerStepper
        steps={steps}
        sx={{
          transform:
            agentIsRequired || agentIsHomeowner
              ? 'translateX(-20px)'
              : 'translateX(-50px)',
        }}
      />
      {agentContact && (
        <Grid style={{ display: 'flex', gap: '.5rem' }}>
          <icons.CheckCircle className={styles.ApprovalFlowTracker__icon} />
          <Typography variant="body1">
            Estimate Released to
            <Typography variant="body1Bold"> Agent</Typography>.
          </Typography>
        </Grid>
      )}
      <Grid style={{ display: 'flex', gap: '.5rem' }}>
        <icons.CheckCircle className={styles.ApprovalFlowTracker__icon} />
        <Typography variant="body1">
          Estimate Released to
          <Typography variant="body1Bold">
            {agentIsHomeowner
              ? ' Agent'
              : homeownerContact?.role === CONTACT_ROLE.HOMEOWNER
                ? ' Homeowner'
                : ' Homebuyer'}
            .
          </Typography>
          {agentIsHomeowner && (
            <Typography
              variant="body1"
              style={{ display: 'inline', marginLeft: '.1rem' }}
            >
              Agent is also the
              <Typography variant="body1Bold"> Homeowner</Typography>
            </Typography>
          )}
        </Typography>
      </Grid>
      <Dialog open={showReStartPayAtCloseModal} onClose={() => setShowReStartPayAtCloseModal(false)}>
        <Box className={styles.modalContainer}>
          <Typography className={styles.modalTitle}>
            Confirmation
          </Typography>
          <Typography className={styles.modalSubtitle}>
            Are you sure you want to Re-start Pay-at-Close?
          </Typography>
          <Box className={styles.modalButtonsContainer}>
            <Box onClick={() => setShowReStartPayAtCloseModal(false)} className={styles.modalCancelBlue}><Typography style={{ color: 'var(--bosscat-blue-600)' }}>No, cancel</Typography></Box>
            <Box onClick={handleConfirmReStartPayAtClose} className={styles.modalAcceptBlue}>{loading ? <Loader size={18} color='#fff' margin='0' /> : `Yes, Re-start`}</Box>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={showReStartPayAtCompletionModal} onClose={() => setShowReStartPayAtCompletionModal(false)}>
        <Box className={styles.modalContainer}>
          <Typography className={styles.modalTitle}>
            Confirmation
          </Typography>
          <Typography className={styles.modalSubtitle}>
            Are you sure you want to Re-start Pay at Completion?
          </Typography>
          <Box className={styles.modalButtonsContainer}>
            <Box onClick={() => setShowReStartPayAtCompletionModal(false)} className={styles.modalCancelBlue}><Typography style={{ color: 'var(--bosscat-blue-600)' }}>No, cancel</Typography></Box>
            <Box onClick={handleConfirmReStartPayAtCompletion} className={styles.modalAcceptBlue}>{loading ? <Loader size={18} color='#fff' margin='0' /> : `Yes, Re-start`}</Box>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={showApproveFinancialModal} onClose={() => setShowApproveFinancialModal(false)}>
        <Box className={styles.modalContainer}>
          <Typography className={styles.modalTitle}>
            Confirmation
          </Typography>
          <Typography className={styles.modalSubtitle}>
            Financing with House Amp was approved?

          </Typography>
          <Box className={styles.modalButtonsContainer}>
            <Box onClick={handleSendRejectFinancing} className={`${styles.modalCancel} ${loading && styles.disabled}`}>{loading ? <Loader size={18} color='#fff' margin='0' /> : <Typography style={{ color: '#fff' }}>No</Typography>}</Box>
            <Box onClick={handleSendApproveFinancing} className={`${styles.modalAccept} ${loading && styles.disabled}`}>{loading ? <Loader size={18} color='#fff' margin='0' /> : `Yes`}</Box>
          </Box>
        </Box>
      </Dialog>
    </Grid>
  )
}

export default ApprovalFlowTracker
